import { useTranslation } from "react-i18next";

type CoverCard = {
  imageUrl: string | null;
  journalName: string | null;
  impactFactor: number | null;
  title: string;
  byline: string;
};

/**
 * A collection's cover, made of its own articles (Figma "Collection / Cover
 * stack"): the first article on the front card, the second and third on the
 * two cards behind it. A collection with fewer articles leaves a glass card
 * that takes the banner's colour. Hovering spreads the cards. The cover is
 * decorative: the list under the header opens the articles.
 *
 * Drawn at the size of an article card and scaled down as a whole, as the
 * design scales it, so the card keeps the type scale of the app.
 */
export function CollectionCoverStack({
  front,
  second,
  third,
}: {
  front: CoverCard;
  second: CoverCard | null;
  third: CoverCard | null;
}): React.JSX.Element {
  return (
    <div aria-hidden className="group relative h-97 w-94.5 shrink-0">
      <span className="absolute top-0 left-0 h-112.5 w-110 origin-top-left scale-86">
        {/* The two back cards hang off the front card, so they take its
            height whatever its content: a paper with no figure has a
            shorter card, and the back cards follow it. */}
        <span className="absolute top-10 left-15 flex w-71 rotate-3 flex-col transition-transform duration-300 group-hover:-translate-y-2 group-hover:rotate-1">
          <span className="absolute -top-4 bottom-4 -left-4 w-59 -rotate-14 transition-transform duration-300 group-hover:-translate-x-6 group-hover:-rotate-16">
            {third === null ? (
              <span className="absolute inset-0 rounded-2xl border border-ds-white/12 bg-ds-white/64 shadow-cover-card-back backdrop-blur-md" />
            ) : (
              <CoverArticleCard
                card={third}
                className="absolute inset-0 overflow-hidden shadow-cover-card-back"
              />
            )}
          </span>
          <span className="absolute -top-4.5 bottom-4.5 left-19 w-59 rotate-5 transition-transform duration-300 group-hover:translate-x-6 group-hover:rotate-11">
            {second === null ? (
              <span className="absolute inset-0 rounded-2xl border border-ds-white/12 bg-ds-white/72 shadow-cover-card-back backdrop-blur-md" />
            ) : (
              <CoverArticleCard
                card={second}
                className="absolute inset-0 overflow-hidden shadow-cover-card-back"
              />
            )}
          </span>
          <CoverArticleCard
            card={front}
            className="relative shadow-cover-card"
          />
        </span>
      </span>
    </div>
  );
}

function CoverArticleCard({
  card,
  className,
}: {
  card: CoverCard;
  className: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span
      className={`flex flex-col gap-2.5 rounded-2xl border border-ds-black/5 bg-ds-white px-3.5 pt-3.5 pb-4 text-left font-dm-sans ${className}`}
    >
      {card.imageUrl === null ? null : (
        <img
          src={card.imageUrl}
          alt=""
          className="h-37.5 w-full shrink-0 rounded-xl bg-ds-slate-50 object-contain"
        />
      )}
      {card.journalName === null ? null : (
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="min-w-0 truncate rounded-full bg-ds-mint-50 px-2 py-0.5 text-caption font-semibold text-ds-mint-800">
            {card.journalName}
          </span>
          {card.impactFactor === null ? null : (
            <span className="shrink-0 text-caption font-medium text-ds-slate-600">
              {t("IF {{value}}", { value: card.impactFactor })}
            </span>
          )}
        </span>
      )}
      <span className="line-clamp-5 text-sm font-semibold text-ds-black">
        {card.title}
      </span>
      <span className="text-caption text-ds-slate-600">{card.byline}</span>
    </span>
  );
}
